import { memo } from 'react';
import { ToDPRVM, ForeignState, ItalianState } from 'mutation/view-models/registration-citizenship';
import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import ArticleList from '../shared/article-list';
import Italian from '../shared/italian';
import Foreign from '../shared/foreign';
import { FormikErrors, FormikTouched } from 'formik';
import { ItemVM } from 'shell/view-models/item';
import DocumentActForm from 'shell/components/shared/act';
import { MotArtCitizenship } from 'shell/models/mutation/mot-art-citizenship';

interface OpenLegisProps {
  values?: ToDPRVM;
  onChange: (key: keyof ToDPRVM, value: ToDPRVM[keyof ToDPRVM]) => void;
  onChangeValues: (values: ToDPRVM) => void;
  errors?: FormikErrors<ToDPRVM>;
  touched?: FormikTouched<ToDPRVM>;
  idReason: string;
  disabled?: boolean;
}

const ToDPRComponent = ({
  onChange,
  values,
  errors,
  touched,
  idReason,
  onChangeValues,
  disabled,
}: OpenLegisProps): React.ReactElement => {
  const onChangeArticleList = useEventCallback((value: ItemVM<MotArtCitizenship> | null) => {
    onChange('article', value);
  });

  const onChangeForeign = useEventCallback(
    (key: keyof ForeignState, value: ForeignState[keyof ForeignState]) => {
      const newValue = {
        ...values?.foreign,
        [key]: value,
      };

      onChange('foreign', newValue);
    }
  );

  const onChangeItalian = useEventCallback(
    (key: keyof ItalianState, value: ItalianState[keyof ItalianState]) => {
      const newValue = {
        ...values?.italian,
        [key]: value,
      };

      onChange('italian', newValue);
    }
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Data acquisto cittadinanza"
          aria-label="Data acquisto cittadinanza"
          value={values?.dateBuying || null}
          onChange={(date) => {
            onChange('dateBuying', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              id="dateBuying"
              {...params}
              fullWidth
              error={touched?.dateBuying && Boolean(errors?.dateBuying)}
              helperText={errors?.dateBuying}
              disabled={disabled}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Data D.P.R. - D.M."
          aria-label="Data D.P.R. - D.M."
          value={values?.dateDPR || null}
          onChange={(date) => {
            onChange('dateDPR', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              id="dateDPR"
              {...params}
              fullWidth
              error={touched?.dateDPR && Boolean(errors?.dateDPR)}
              helperText={errors?.dateDPR}
              disabled={disabled}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <ArticleList
          value={values?.article?.id?.toString() || ''}
          onChange={onChangeArticleList}
          idReason={idReason}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Giuramento reso il"
          aria-label="Giuramento reso il"
          value={values?.dateOath || null}
          onChange={(date) => {
            onChange('dateOath', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              id="dateOath"
              {...params}
              fullWidth
              error={touched?.dateOath && Boolean(errors?.dateOath)}
              helperText={errors?.dateOath}
              disabled={disabled}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={1} style={{ display: 'flex', alignItems: 'center' }}>
        IN
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeign"
            defaultValue={false}
            value={values?.isForeign}
            onChange={() => onChange('isForeign', !values?.isForeign)}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeign ? (
        <Foreign onChange={onChangeForeign} values={values?.foreign} disabled={disabled} />
      ) : (
        <Italian
          onChange={onChangeItalian}
          values={values?.italian}
          dateFilter={values?.dateOath}
          disabled={disabled}
        />
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <DocumentActForm
        values={values}
        onChange={(key, value) => {
          onChange(key, value);
        }}
        onChanges={(fields) => {
          onChangeValues(fields);
        }}
        errors={errors}
        touched={touched}
        requiredDigital={{
          documentDate: false,
        }}
        disabled={disabled}
      />
    </Grid>
  );
};

ToDPRComponent.displayName = 'Form Accordion Registration Citizenship - to DPR component';

const ToDPR = memo(ToDPRComponent);

export default ToDPR;
